import React from 'react';
import { Box, TextField, Grid, Slider, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AlignHorizontalLeft, AlignHorizontalRight, AlignVerticalBottom, AlignVerticalTop } from '@mui/icons-material';

function BlockProps({ properties = {}, onPropertyChange }) {
  // Specific handler for padding changes
  const handlePaddingChange = (side, value) => {
    const currentPadding = properties.padding || { top: 0, right: 0, bottom: 0, left: 0 };
    const newPadding = { ...currentPadding, [side]: parseInt(value) || 0 };
    onPropertyChange('padding', newPadding);
  };

  // Specific handler for margin changes
  const handleMarginChange = (side, value) => {
    const currentMargin = properties.margin || { top: 0, right: 0, bottom: 0, left: 0 };
    const newMargin = { ...currentMargin, [side]: parseInt(value) || 0 };
    onPropertyChange('margin', newMargin);
  };

  return (
    <Accordion sx={{ mb: 2,backgroundColor: 'rgba(0, 0, 0, .03)' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
          Block Properties
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 400 }}>
              Background Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <input
                type="color"
                value={properties.backgroundColor || '#ffffff'}
                onChange={(e) => onPropertyChange('backgroundColor', e.target.value)}
                style={{
                  width: 40,
                  height: 40,
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              />
              <TextField
                value={properties.backgroundColor || 'transparent'}
                onChange={(e) => onPropertyChange('backgroundColor', e.target.value)}
                size="small"
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 400 }}>
              Padding
            </Typography>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignVerticalTop />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.padding?.top || 0}
                  onChange={(e, value) => handlePaddingChange('top', value)}
                  aria-labelledby="input-slider-padding-top"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.padding?.top || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignHorizontalRight />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.padding?.right || 0}
                  onChange={(e, value) => handlePaddingChange('right', value)}
                  aria-labelledby="input-slider-padding-right"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.padding?.right || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignVerticalBottom />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.padding?.bottom || 0}
                  onChange={(e, value) => handlePaddingChange('bottom', value)}
                  aria-labelledby="input-slider-padding-bottom"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.padding?.bottom || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignHorizontalLeft />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.padding?.left || 0}
                  onChange={(e, value) => handlePaddingChange('left', value)}
                  aria-labelledby="input-slider-padding-left"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.padding?.left || 0} px
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Margin */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 400 }}>
              Margin
            </Typography>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignVerticalTop />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.margin?.top || 0}
                  onChange={(e, value) => handleMarginChange('top', value)}
                  aria-labelledby="input-slider-margin-top"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.margin?.top || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignHorizontalRight />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.margin?.right || 0}
                  onChange={(e, value) => handleMarginChange('right', value)}
                  aria-labelledby="input-slider-margin-right"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.margin?.right || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignVerticalBottom />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.margin?.bottom || 0}
                  onChange={(e, value) => handleMarginChange('bottom', value)}
                  aria-labelledby="input-slider-margin-bottom"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.margin?.bottom || 0} px
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid>
                <AlignHorizontalLeft />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={properties.margin?.left || 0}
                  onChange={(e, value) => handleMarginChange('left', value)}
                  aria-labelledby="input-slider-margin-left"
                  step={1}
                  marks
                  max={80}
                  min={0}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" gutterBottom>
                  {properties.margin?.left || 0} px
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default BlockProps;